import type { Site, Vehicle } from "@fts/core";
import { Icon, type IconName } from "../components/Icon";
import { PageHeader, Panel } from "../components/ui";
import { useI18n } from "../i18n";
import { canSeeKpiPanel } from "../lib/permissions";
import { navigate, type Route } from "../router/hash";
import { useSession, type TenantData } from "../state/session";
import { KpiPanelView } from "./KpiPanel";
import { OverviewLive } from "./OverviewLive";
import { SitesTable } from "./SitesTable";

function Kpi({ icon, label, value, unit, sub }: { icon: IconName; label: string; value: string; unit?: string; sub: string }) {
  return (
    <div className="kpi">
      <div className="kpi-head">
        <span className="kpi-label">{label}</span>
        <span className="kpi-icon">
          <Icon name={icon} size={18} />
        </span>
      </div>
      <div className="kpi-value">
        <span>{value}</span>
        {unit && <span className="kpi-unit">{unit}</span>}
      </div>
      <div className="kpi-sub">{sub}</div>
    </div>
  );
}

export function computeKpis(data: Pick<TenantData, "sites" | "vehicles" | "members">) {
  const { sites, vehicles, members } = data;
  const rootSites = sites.filter((s: Site) => !s.parentId || !sites.some((p) => p.id === s.parentId)).length;
  const active = vehicles.filter((v: Vehicle) => v.active).length;
  const withDevice = vehicles.filter((v) => !!v.deviceId).length;
  return {
    sites: sites.length,
    rootSites,
    vehicles: vehicles.length,
    active,
    inactive: vehicles.length - active,
    withDevice,
    withoutDevice: vehicles.length - withDevice,
    users: members?.length ?? null,
    admins: members?.filter((m) => m.role === "admin").length ?? null,
  };
}

/** The KPI panel's site filter (same rule as KpiPanelView: unknown ids mean "all sites"). */
function kpiSiteId(data: TenantData, route: Route): string | null {
  const site = route.query.get("site");
  return site && data.sites.some((s) => s.id === site) ? site : null;
}

/**
 * Home. Fleet managers and above land on the manager KPI panel (ADR 0018); the fleet summary is the
 * second tab (and the only view for other roles).
 */
export function Dashboard({ data, route }: { data: TenantData; route?: Route }) {
  const { t } = useI18n();
  const { tenant, role } = useSession();
  const kpi = canSeeKpiPanel(role);
  const tab = kpi && route?.query.get("tab") !== "summary" ? "kpi" : "summary";
  return (
    <>
      <PageHeader title={t("dashboard.title")} subtitle={t("dashboard.subtitle", { tenant: tenant?.name ?? "" })} />
      {kpi && (
        <div className="tabs" role="tablist" aria-label={t("dashboard.tabs")}>
          <button type="button" role="tab" aria-selected={tab === "kpi"} className={`tab${tab === "kpi" ? " tab-on" : ""}`} onClick={() => navigate("/", { tab: "kpi" })}>
            {t("kpiPanel.title")}
          </button>
          <button type="button" role="tab" aria-selected={tab === "summary"} className={`tab${tab === "summary" ? " tab-on" : ""}`} onClick={() => navigate("/", { tab: "summary" })}>
            {t("dashboard.summaryTab")}
          </button>
        </div>
      )}
      {tab === "kpi" && route ? (
        <>
          <KpiPanelView data={data} route={route} />
          <OverviewLive data={data} siteId={kpiSiteId(data, route)} />
        </>
      ) : (
        <FleetSummary data={data} />
      )}
    </>
  );
}

function FleetSummary({ data }: { data: TenantData }) {
  const { t, locale } = useI18n();
  const k = computeKpis(data);
  const n = (x: number) => x.toLocaleString(locale);

  return (
    <>
      <div className="kpi-grid">
        <Kpi icon="building" label={t("kpi.sites")} value={n(k.sites)} sub={t("kpi.sitesSub", { count: n(k.rootSites) })} />
        <Kpi icon="truck" label={t("kpi.vehicles")} value={n(k.vehicles)} sub={t("kpi.vehiclesSub", { active: n(k.active), inactive: n(k.inactive) })} />
        <Kpi
          icon="chip"
          label={t("kpi.devices")}
          value={n(k.withDevice)}
          unit={`/${n(k.vehicles)}`}
          sub={t("kpi.devicesSub", { count: n(k.withoutDevice) })}
        />
        {k.users !== null && k.admins !== null && (
          <Kpi icon="users" label={t("kpi.users")} value={n(k.users)} sub={t("kpi.usersSub", { count: n(k.admins) })} />
        )}
      </div>
      <OverviewLive data={data} />
      <Panel title={t("dashboard.sitesTitle")} flush>
        <SitesTable sites={data.sites} vehicles={data.vehicles} />
      </Panel>
    </>
  );
}
